import graphql__f750ce653c593d0f5a38 from "./__generated__/MapWithSidebarLayout_map.graphql.js";import { createContext, useCallback, useEffect, useRef, useState } from "react";
import { graphql, useFragment } from "react-relay";
import { useDispatch, useSelector } from "react-redux";
import { ZoomControl } from "react-leaflet";
import classNames from "classnames";
import { throttle, isEmpty } from "lodash";

import MapBase from "./MapBase";
import MapPanelSpinner from "./MapPanel/MapPanelSpinner";
import PushableContainer from "../Utils/PushableContainer";
import { useMapContext } from "./MapContext";
import { useGetStickyElementsHeight } from "../Utils/StickyElementsHeight";

import { setFullScreenMode } from "../../reducers/styling";

export const MapWithSidebarLayoutContext = createContext({});

const MapWithSidebarLayout = props => {
  const map = useFragment(MapWithSidebarLayout_map, props.map);

  const { mapKey } = useMapContext();
  const { children, onElementClick } = props;

  const pushableContainersOffset = useSelector(
    ({ styling }) => styling.pushableContainersOffset
  );
  const affixHeaderOffsetTop = useSelector(({ styling }) => styling.affixHeaderOffsetTop);
  const hasActivePanels = useSelector(({ map }) => !isEmpty(map[mapKey].panels));
  const fullScreenMode = useSelector(({ styling }) => styling.fullScreenMode);

  const [containerHeight, setContainerHeight] = useState(600);
  const [isReady, setIsReady] = useState(false);
  const [mapSidebarContainer, setMapSidebarContainer] = useState(null);
  const isUnmountedRef = useRef(false);

  const getStickyElementsHeight = useGetStickyElementsHeight();

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const reprocessSizes = useCallback(
    throttle(() => {
      if (isUnmountedRef.current) return;

      const stickyElementsHeight = getStickyElementsHeight({
        ignoreStickyTitleBar: true
      });

      const newContainerHeight = window.innerHeight - stickyElementsHeight;

      if (newContainerHeight !== containerHeight) setContainerHeight(newContainerHeight);
      if (newContainerHeight && !isReady) setIsReady(true);
    }, 100),
    []
  );

  const dispatch = useDispatch();

  // Ensure that the map updates the layout so it's shown in full screen mode, with the
  // top bar taking the whole window width, and without the footer bar.
  //
  // Note that we use the `fullScreenMode` variable from the redux store to ensure that if
  // another component sets it to false while unmounting, we reset it to true to ensure
  // it's not lost. This is needed because React unmounts the previous instance of
  // MapWithSidebarLayout after the new one has been mounted when passing the parent
  // component re-renders the whole tree, like when passing from mobile to desktop view.
  useEffect(() => {
    if (!fullScreenMode) dispatch(setFullScreenMode(true));

    return () => fullScreenMode && dispatch(setFullScreenMode(false));
  }, [fullScreenMode, dispatch]);

  const sizeListenersBound = useRef(false);

  useEffect(() => {
    window.addEventListener("scroll", reprocessSizes, false);
    window.addEventListener("resize", reprocessSizes, false);

    sizeListenersBound.current = true;

    // Add map html class
    const html = document.getElementsByTagName("html")[0];
    html.classList.add("map");

    reprocessSizes();

    return () => {
      // Remove map html class
      const htmlNode = document.getElementsByTagName("html")[0];
      htmlNode.classList.remove("map");

      if (sizeListenersBound.current) {
        window.removeEventListener("scroll", reprocessSizes);
        window.removeEventListener("resize", reprocessSizes);

        sizeListenersBound.current = false;
      }
    };
  }, [dispatch, reprocessSizes]);

  // Reprocess the container global size as soon as one of the layout dependent parameters
  // are updated.
  useEffect(reprocessSizes, [
    reprocessSizes,
    affixHeaderOffsetTop,
    pushableContainersOffset,
    isReady
  ]);

  // Mark the component as unmounted when it is, to avoid the throttled size reprocessing
  // to run on the unmounted component, which creates unmounted component state update
  // errors.
  useEffect(() => () => (isUnmountedRef.current = true), []);

  if (!isReady) return null;

  const mapSidebarClassName = classNames([
    "map-sidebar-container",
    {
      active: hasActivePanels
    }
  ]);

  return (
    <PushableContainer>
      <div className="map-wrapper d-flex" style={{ height: containerHeight }}>
        <div className={mapSidebarClassName}>
          {hasActivePanels || <MapPanelSpinner />}
          <div className="map-sidebar-container-inner" ref={setMapSidebarContainer} />
        </div>

        {mapSidebarContainer && (
          <div className="flex-1">
            <MapWithSidebarLayoutContext.Provider value={{ mapSidebarContainer }}>
              <MapBase
                map={map}
                keyboard={false}
                height="100%"
                mapProps={{ zoomControl: false }}
                onElementClick={onElementClick}
              >
                <ZoomControl position="topright" />

                {children}
              </MapBase>
            </MapWithSidebarLayoutContext.Provider>
          </div>
        )}
      </div>
    </PushableContainer>
  );
};

const MapWithSidebarLayout_map = graphql__f750ce653c593d0f5a38;

export default MapWithSidebarLayout;
